import {
  AccessTime as AccessTimeIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CancelButton from '../components/CancelButton';
import Loading from '../components/Loading';
import { useAuth } from '../contexts/AuthContext';
import { useDietitianData } from '../hooks/useDietitianData';
import { ClientRelationship, clientRelationshipApi } from '../services/clientRelationshipApi';
import { ClientRequest, clientRequestApi } from '../services/clientRequestApi';
import { DisconnectionRequest, disconnectionRequestApi } from '../services/disconnectionRequestApi';
import { colors } from '../theme';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`requests-tabpanel-${index}`}
      aria-labelledby={`requests-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const DietitianClientRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const { dietitianId, showSuccess, showError } = useDietitianData({
    fetchClients: false,
    fetchAppointments: false,
    fetchDietPrograms: false,
  });
  const { setClientsRefreshTrigger } = useAuth();

  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [requests, setRequests] = useState<ClientRequest[]>([]);
  const [relationships, setRelationships] = useState<ClientRelationship[]>([]);
  const [disconnectionRequests, setDisconnectionRequests] = useState<DisconnectionRequest[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<ClientRequest | null>(null);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestAction, setRequestAction] = useState<'accept' | 'reject' | null>(null);
  const [responseMessage, setResponseMessage] = useState('');

  const loadData = useCallback(async () => {
    if (!dietitianId) return;

    try {
      setLoading(true);
      const [requestsData, relationshipsData, disconnectionData] = await Promise.all([
        clientRequestApi.getClientRequestsByDietitian(dietitianId),
        clientRelationshipApi.getRelationshipsByDietitian(dietitianId),
        disconnectionRequestApi.getDisconnectionRequestsByDietitian(dietitianId),
      ]);
      setRequests(requestsData);
      setRelationships(relationshipsData);
      setDisconnectionRequests(disconnectionData);
    } catch (err: any) {
      console.error('Veri yüklenemedi:', err);
      showError(err.message || 'Veri yüklenirken bir hata oluştu.', 'Hata');
    } finally {
      setLoading(false);
    }
  }, [dietitianId, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const filteredRequests = requests.filter((req) => {
    const matchesStatus = filterStatus === 'all' || req.status === filterStatus;
    const matchesSearch =
      searchQuery === '' ||
      req.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      req.clientEmail.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const activeRelationships = relationships.filter((rel) => rel.status === 'Active');
  const pendingDisconnections = relationships.filter((rel) => rel.status === 'PendingDisconnection');
  const pendingRequestsCount = requests.filter((r) => r.status === 'Pending').length;

  const handleOpenRequestDialog = (request: ClientRequest, action: 'accept' | 'reject') => {
    setSelectedRequest(request);
    setRequestAction(action);
    setResponseMessage('');
    setRequestDialogOpen(true);
  };

  const handleAcceptRequest = async () => {
    if (!selectedRequest) return;

    try {
      await clientRequestApi.acceptClientRequest(selectedRequest.id, {
        message: responseMessage.trim() || undefined,
      });
      showSuccess('İstek kabul edildi!', 'Başarılı');
      setClientsRefreshTrigger((t) => t + 1);
      await loadData();
      setRequestDialogOpen(false);
      setSelectedRequest(null);
      setResponseMessage('');
    } catch (err: any) {
      showError(err.message || 'İstek kabul edilemedi', 'Hata');
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest) return;

    try {
      await clientRequestApi.rejectClientRequest(selectedRequest.id, {
        message: responseMessage.trim() || undefined,
      });
      showSuccess('İstek reddedildi.', 'Başarılı');
      await loadData();
      setRequestDialogOpen(false);
      setSelectedRequest(null);
      setResponseMessage('');
    } catch (err: any) {
      showError(err.message || 'İstek reddedilemedi', 'Hata');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'warning';
      case 'Accepted':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Active':
        return 'success';
      case 'PendingDisconnection':
        return 'warning';
      case 'Disconnected':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'Beklemede';
      case 'Accepted':
        return 'Kabul Edildi';
      case 'Rejected':
        return 'Reddedildi';
      case 'Active':
        return 'Aktif';
      case 'PendingDisconnection':
        return 'Ayrılma Bekliyor';
      case 'Disconnected':
        return 'Ayrıldı';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="İstekler yükleniyor..."
      />
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          p: 3,
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          mb: 3,
          flexShrink: 0,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          flex={1}
          minWidth={0}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: colors.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PersonAddIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box minWidth={0} flex={1}>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ color: colors.text.primary }}
              noWrap
            >
              İstek Yönetimi
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Danışan isteklerini yönetin ve ilişki durumlarını takip edin
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card
            elevation={1}
            sx={{
              borderRadius: 2,
              border: `1px solid ${colors.border?.light || '#e0e0e0'}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px)',
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    bgcolor: colors.primary.light,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PersonAddIcon sx={{ color: colors.primary.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: colors.text.primary }}>
                    {pendingRequestsCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bekleyen İstek
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card
            elevation={1}
            sx={{
              borderRadius: 2,
              border: `1px solid ${colors.border?.light || '#e0e0e0'}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px)',
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    bgcolor: colors.primary.light,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PeopleIcon sx={{ color: colors.primary.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: colors.text.primary }}>
                    {activeRelationships.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aktif İlişki
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card
            elevation={1}
            sx={{
              borderRadius: 2,
              border: `1px solid ${colors.border?.light || '#e0e0e0'}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px)',
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    bgcolor: colors.status.warning.background,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PersonRemoveIcon sx={{ color: colors.status.warning.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: colors.text.primary }}>
                    {pendingDisconnections.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ayrılma Bekliyor
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 64,
              fontSize: '0.95rem',
              color: colors.text.secondary,
              '& svg': {
                color: 'inherit',
              },
              '&:hover': {
                bgcolor: colors.primary.main,
                color: 'white !important',
                '& svg': {
                  color: 'white !important',
                },
              },
            },
            '& .Mui-selected': {
              color: `${colors.primary.main} !important`,
              '& svg': {
                color: `${colors.primary.main} !important`,
              },
              '&:hover': {
                bgcolor: colors.primary.main,
                color: 'white !important',
                '& svg': {
                  color: 'white !important',
                },
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: colors.primary.main,
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddIcon />
                <span>Danışan İstekleri ({filteredRequests.length})</span>
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon />
                <span>Aktif İlişkiler ({activeRelationships.length})</span>
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonRemoveIcon />
                <span>Ayrılma İstekleri ({pendingDisconnections.length})</span>
              </Box>
            }
          />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        {/* Filters */}
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                placeholder="Danışan ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Durum Filtrele</InputLabel>
                <Select
                  value={filterStatus}
                  label="Durum Filtrele"
                  onChange={(e) => setFilterStatus(e.target.value)}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="Pending">Beklemede</MenuItem>
                  <MenuItem value="Accepted">Kabul Edildi</MenuItem>
                  <MenuItem value="Rejected">Reddedildi</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Requests List */}
        {filteredRequests.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <PersonAddIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                İstek bulunamadı
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchQuery || filterStatus !== 'all'
                  ? 'Arama kriterlerinize uygun istek bulunamadı.'
                  : 'Henüz hiç istek bulunmuyor.'}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {filteredRequests.map((request) => (
              <Card
                key={request.id}
                elevation={2}
                sx={{
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, display: 'flex', gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: colors.primary.light,
                          width: 56,
                          height: 56,
                          fontSize: '1.5rem',
                          fontWeight: 600,
                        }}
                      >
                        {request.clientName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="h6" fontWeight={600} sx={{ color: colors.text.primary }}>
                            {request.clientName}
                          </Typography>
                          <Chip
                            label={getStatusLabel(request.status)}
                            color={getStatusColor(request.status) as any}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <EmailIcon sx={{ fontSize: 16, color: colors.text.secondary }} />
                          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                            {request.clientEmail}
                          </Typography>
                        </Box>
                        {request.message && (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 1,
                              p: 1.5,
                              bgcolor: colors.background.light,
                              borderRadius: 1,
                              fontStyle: 'italic',
                              color: colors.text.secondary,
                            }}
                          >
                            "{request.message}"
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 14, color: colors.text.secondary }} />
                          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                            {new Date(request.createdAt).toLocaleString('tr-TR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    {request.status === 'Pending' && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleOpenRequestDialog(request, 'accept')}
                          sx={{ textTransform: 'none', borderRadius: 2, width: 'fit-content' }}
                        >
                          Kabul Et
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => handleOpenRequestDialog(request, 'reject')}
                          sx={{ textTransform: 'none', borderRadius: 2, width: 'fit-content' }}
                        >
                          Reddet
                        </Button>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {activeRelationships.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Aktif ilişki bulunamadı
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {activeRelationships.map((relationship) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={relationship.id}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: colors.primary.light,
                          width: 56,
                          height: 56,
                          fontSize: '1.5rem',
                          fontWeight: 600,
                          color: colors.primary.main,
                        }}
                      >
                        {relationship.clientName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: colors.text.primary }}>
                          {relationship.clientName}
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                          {relationship.clientEmail}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={getStatusLabel(relationship.status)}
                      color={getStatusColor(relationship.status) as any}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => navigate(`/dietitian/clients/${relationship.clientId}`)}
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      Detayları Gör
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {pendingDisconnections.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <PersonRemoveIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Bekleyen ayrılma isteği bulunamadı
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {pendingDisconnections.map((relationship) => {
              const disconnectionRequest = disconnectionRequests.find(
                (req) =>
                  req.clientId === relationship.clientId &&
                  req.dietitianId === relationship.dietitianId &&
                  req.status === 'Pending'
              );

              return (
                <Card
                  key={relationship.id}
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'warning.main',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: colors.status.warning.light,
                          width: 56,
                          height: 56,
                          fontSize: '1.5rem',
                          fontWeight: 600,
                        }}
                      >
                        {relationship.clientName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: colors.text.primary }}>
                          {relationship.clientName}
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.text.secondary }} gutterBottom>
                          {relationship.clientEmail}
                        </Typography>
                        {disconnectionRequest && (
                          <>
                            <Chip
                              label={
                                disconnectionRequest.requestedBy === 'Client'
                                  ? 'Danışan Tarafından'
                                  : 'Siz Tarafından'
                              }
                              color="warning"
                              size="small"
                              sx={{ mt: 1, mr: 1 }}
                            />
                            {disconnectionRequest.reason && (
                              <Box
                                sx={{
                                  mt: 2,
                                  p: 2,
                                  bgcolor: colors.status.warning.background,
                                  borderRadius: 1,
                                  border: `1px solid ${colors.status.warning.border}`,
                                }}
                              >
                                <Typography variant="body2" fontWeight={600} gutterBottom sx={{ color: colors.text.primary }}>
                                  Sebep:
                                </Typography>
                                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                                  {disconnectionRequest.reason}
                                </Typography>
                              </Box>
                            )}
                            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: colors.text.secondary }}>
                              Otomatik onay: {new Date(disconnectionRequest.autoApproveAt).toLocaleString('tr-TR')}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                    {disconnectionRequest && disconnectionRequest.requestedBy === 'Client' && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          onClick={async () => {
                            if (disconnectionRequest) {
                              try {
                                await disconnectionRequestApi.approveDisconnectionRequest(
                                  disconnectionRequest.id,
                                  {}
                                );
                                showSuccess('Ayrılma isteği onaylandı', 'Başarılı');
                                await loadData();
                              } catch (err: any) {
                                showError(err.message || 'İstek onaylanamadı', 'Hata');
                              }
                            }
                          }}
                          sx={{ textTransform: 'none', borderRadius: 2 }}
                        >
                          Onayla
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          fullWidth
                          onClick={async () => {
                            if (disconnectionRequest) {
                              try {
                                await disconnectionRequestApi.rejectDisconnectionRequest(
                                  disconnectionRequest.id,
                                  {}
                                );
                                showSuccess('Ayrılma isteği reddedildi', 'Başarılı');
                                await loadData();
                              } catch (err: any) {
                                showError(err.message || 'İstek reddedilemedi', 'Hata');
                              }
                            }
                          }}
                          sx={{ textTransform: 'none', borderRadius: 2 }}
                        >
                          Reddet
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </TabPanel>

      {/* Request Dialog */}
      <Dialog
        open={requestDialogOpen}
        onClose={() => {
          setRequestDialogOpen(false);
          setSelectedRequest(null);
          setResponseMessage('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
            color: 'white',
            py: 2,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {requestAction === 'accept' ? 'İsteği Kabul Et' : 'İsteği Reddet'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedRequest && (
            <Box>
              <Typography variant="body1" gutterBottom sx={{ color: colors.text.primary }}>
                <strong>{selectedRequest.clientName}</strong> isteğini{' '}
                {requestAction === 'accept' ? 'kabul' : 'reddet'}mek istediğinize emin misiniz?
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Mesaj (Opsiyonel)"
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                sx={{ mt: 2 }}
                placeholder="Danışana göndermek istediğiniz mesajı yazın..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <CancelButton
            onClick={() => {
              setRequestDialogOpen(false);
              setSelectedRequest(null);
              setResponseMessage('');
            }}
          />
          <Button
            onClick={requestAction === 'accept' ? handleAcceptRequest : handleRejectRequest}
            variant="contained"
            color={requestAction === 'accept' ? 'success' : 'error'}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {requestAction === 'accept' ? 'Kabul Et' : 'Reddet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DietitianClientRequestsPage;
